/**
 * Subscription Dashboard - Comprehensive subscription and usage management
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Divider,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  useTheme,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  AddCircleOutline as AddonIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';

import Layout from '../../components/layout/Layout';
import UsageWidget from '../../components/saas/UsageWidget';
import SubscriptionPlanCard from '../../components/saas/SubscriptionPlanCard';
import subscriptionService from '../../services/subscriptionService';
import usageService from '../../services/usageService';
import companySubscriptionService from '../../services/companySubscriptionService';
import { createAddonOrder, verifyPayment } from '../../services/paymentService';
import locationService from '../../services/locationService';
import productService from '../../services/productService';
import axiosInstance from '../../utils/axios';
import API_CONFIG from '../../config/api.config';
import { getAuthHeader } from '../../services/authService';
import { formatCurrency } from '../../utils/currencyFormatter';
import axios from 'axios';

/**
 * Current Subscription Card
 */
const CurrentSubscriptionCard = ({ subscription, onUpgrade, onCancel, loading }) => {
  const theme = useTheme();

  if (!subscription) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
          <Typography variant="h6" color="text.secondary" fontFamily="Roboto">
            No active subscription found
          </Typography>
          <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={() => onUpgrade && onUpgrade()}>
            View Plans
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { plan, status, trialStartDate, trialEndDate, currentPeriodStart, currentPeriodEnd, isInTrial, trialDaysRemaining } = subscription;

  const getStatusChip = () => {
    switch (status) {
      case 'TRIAL':
        return <Chip label="Trial Period" color="info" icon={<ScheduleIcon />} sx={{ fontWeight: 600 }} />;
      case 'ACTIVE':
        return <Chip label="Active" color="success" icon={<CheckIcon />} sx={{ fontWeight: 600 }} />;
      case 'PAST_DUE':
        return <Chip label="Past Due" color="warning" icon={<WarningIcon />} sx={{ fontWeight: 600 }} />;
      case 'CANCELLED':
        return <Chip label="Cancelled" color="error" icon={<CancelIcon />} sx={{ fontWeight: 600 }} />;
      default:
        return <Chip label={status} color="default" sx={{ fontWeight: 600 }} />;
    }
  };

  const parseDate = (value) => {
    if (!value) return null;
    if (Array.isArray(value) && value.length >= 3) {
      const [y, m, d, h = 0, min = 0, s = 0] = value;
      return new Date(y, m - 1, d, h, min, s || 0);
    }
    if (typeof value === 'string') {
      const iso = value.includes(' ') && !value.includes('T') ? value.replace(' ', 'T') : value;
      const d = new Date(iso);
      return isNaN(d.getTime()) ? null : d;
    }
    if (value instanceof Date) return value;
    return null;
  };

  const formatDate = (value) => {
    const d = parseDate(value);
    if (!d) return 'N/A';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Calculate relative dates
  const periodLabel = (isInTrial || status === 'TRIAL') ? 'Trial Period' : 'Billing Period';
  const periodStart = (isInTrial || status === 'TRIAL') ? trialStartDate : currentPeriodStart;
  const periodEnd = (isInTrial || status === 'TRIAL') ? trialEndDate : currentPeriodEnd;

  const totalDays = (() => {
    const s = parseDate(periodStart);
    const e = parseDate(periodEnd);
    if (!s || !e) return null;
    const ms = e.getTime() - s.getTime();
    return Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)));
  })();

  const daysUsed = (() => {
    const s = parseDate(periodStart);
    if (!s) return 0;
    const ms = Date.now() - s.getTime();
    const days = Math.floor(ms / (1000 * 60 * 60 * 24)) + 1;
    return totalDays ? Math.min(totalDays, days) : days;
  })();

  const daysLeft = totalDays ? Math.max(0, totalDays - daysUsed) : null;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Decorative gradient top bar */}
      <Box
        sx={{
          height: 8,
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
        }}
      />

      <CardContent sx={{ p: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={7}>
            <Box display="flex" flexDirection="column" height="100%">
              <Box mb={3}>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                  <Typography variant="h4" fontWeight={700} fontFamily="Roboto">
                    {plan.name}
                  </Typography>
                  {getStatusChip()}
                </Box>
                <Typography variant="body1" color="text.secondary" fontFamily="Roboto">
                  {plan.description || 'Premium subscription plan'}
                </Typography>
              </Box>

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Box p={2} bgcolor={alpha(theme.palette.primary.main, 0.05)} borderRadius={2}>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={600} display="block" gutterBottom>
                      {periodLabel}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formatDate(periodStart)} - {formatDate(periodEnd)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box p={2} bgcolor={alpha(theme.palette.warning.main, 0.05)} borderRadius={2}>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontWeight={600} display="block" gutterBottom>
                      Next Billing
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formatDate((isInTrial || status === 'TRIAL') ? trialEndDate : currentPeriodEnd)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Box mt="auto">
                <Typography variant="subtitle2" gutterBottom fontFamily="Roboto" fontWeight={600}>
                  Active Features
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {plan.features && Object.keys(plan.features).length > 0 ? (
                    Object.entries(plan.features)
                      .filter(([, v]) => v)
                      .slice(0, 5) // Show top 5 features
                      .map(([key]) => (
                        <Chip
                          key={key}
                          label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          size="small"
                          icon={<CheckIcon />}
                          sx={{ bgcolor: 'transparent', border: '1px solid', borderColor: 'divider' }}
                        />
                      ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">Standard features included</Typography>
                  )}
                  {plan.features && Object.keys(plan.features).length > 5 && (
                    <Chip label={`+${Object.keys(plan.features).length - 5} more`} size="small" variant="outlined" />
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, mx: 2 }} />

          <Grid item xs={12} md={4}>
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center'
              }}
            >
              <Typography variant="body2" color="text.secondary" fontWeight={500} gutterBottom>
                CURRENT PRICE
              </Typography>
              <Typography variant="h3" fontWeight={800} color="primary" fontFamily="Roboto">
                {formatCurrency(plan.priceMonthly, { currencyCode: plan.currency })}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                per month
              </Typography>

              <Box width="100%" display="flex" flexDirection="column" gap={2}>
                {/* Upgrade button hidden as per current logic (passed as empty func), keeping Cancel */}
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={onCancel}
                  disabled={loading || status === 'CANCELLED'}
                  fullWidth
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Cancel Subscription
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CardContent >
    </Card >
  );
};

/**
 * Subscription History Card
 */
const SubscriptionHistoryCard = ({ history, loading }) => {
  if (loading) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!history || history.length === 0) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box textAlign="center" py={6}>
            <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No subscription history
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Past subscription records will appear here.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box p={2} bgcolor="background.default" borderBottom={1} borderColor="divider">
        <Typography variant="h6" fontWeight={600} fontFamily="Roboto">
          Subscription History
        </Typography>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Plan</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((sub, index) => (
              <TableRow key={index} hover>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {sub.plan.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {sub.plan.description || 'Monthly Subscription'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={sub.status}
                    size="small"
                    color={sub.status === 'ACTIVE' ? 'success' : sub.status === 'CANCELLED' ? 'error' : 'default'}
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(sub.createdAt).toLocaleDateString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(sub.createdAt).toLocaleTimeString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrency(sub.plan.priceMonthly, { currencyCode: sub.plan.currency })}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

/**
 * Plan Upgrade Dialog
 */
const PlanUpgradeDialog = ({ open, onClose, currentPlan, onUpgrade, loading }) => {
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    if (open) {
      loadPlans();
    }
  }, [open]);

  const loadPlans = async () => {
    try {
      const plansData = await subscriptionService.getSubscriptionPlans();
      const filtered = Array.isArray(plansData)
        ? plansData
          .filter(plan => plan.id !== currentPlan?.id)
          .filter(p => String(p.status || 'ACTIVE').toUpperCase() === 'ACTIVE')
        : [];
      setPlans(filtered);
    } catch (error) {
      console.error('Error loading plans:', error);
      toast.error('Failed to load subscription plans');
    } finally {
      setPlansLoading(false);
    }
  };

  const handleUpgrade = () => {
    if (selectedPlan && onUpgrade) {
      onUpgrade(selectedPlan);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Upgrade Your Plan
      </DialogTitle>

      <DialogContent>
        {plansLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {plans.map((plan) => (
              <Grid item xs={12} md={6} lg={4} key={plan.id}>
                <SubscriptionPlanCard
                  plan={plan}
                  recommended={plan.isPopular}
                  onSelectPlan={setSelectedPlan}
                  currentPlan={selectedPlan?.id === plan.id}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpgrade}
          disabled={loading || !selectedPlan}
        >
          {loading ? 'Upgrading...' : 'Upgrade Plan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Cancellation Dialog
 */
const CancellationDialog = ({ open, onClose, onConfirm, loading }) => {
  const [reason, setReason] = useState('');

  const handleConfirm = () => {
    onConfirm(reason || 'User requested cancellation');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Cancel Subscription
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Are you sure you want to cancel your subscription? This action cannot be undone.
        </Alert>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Your subscription will remain active until the end of your current billing period.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Keep Subscription
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={loading}
        >
          {loading ? 'Cancelling...' : 'Cancel Subscription'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Main Subscription Dashboard Component
 */
const SubscriptionDashboard = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionHistory] = useState([]); // History tab hidden for now
  const [usageData, setUsageData] = useState(null);
  const [addonDefinitions, setAddonDefinitions] = useState([]);
  const [myAddons, setMyAddons] = useState([]);
  const [addonsLoading, setAddonsLoading] = useState(false);
  const [addonDialogOpen, setAddonDialogOpen] = useState(false);
  const [addonDialogDef, setAddonDialogDef] = useState(null);
  const [addonQuantity, setAddonQuantity] = useState(1);
  const [addonDuration, setAddonDuration] = useState('REMAINING');
  const [addonPreview, setAddonPreview] = useState(null);
  const [addonPreviewLoading, setAddonPreviewLoading] = useState(false);
  const [addonPaymentLoading, setAddonPaymentLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [error, setError] = useState(null);
  const theme = useTheme();

  const isTrialUser = subscription && (subscription.isInTrial || subscription.status === 'TRIAL');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (currentTab === 2 && subscription && !isTrialUser) {
      loadAddons();
    }
  }, [currentTab, subscription, isTrialUser]); // tab 2 = Add-ons

  // Load Razorpay script when addon dialog is open
  useEffect(() => {
    if (!addonDialogOpen) return;
    if (window.Razorpay) return;
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => { try { document.body.removeChild(script); } catch (e) { } };
  }, [addonDialogOpen]);

  // Fetch addon price preview when dialog options change
  useEffect(() => {
    if (!addonDialogOpen || !addonDialogDef?.id) {
      setAddonPreview(null);
      return;
    }
    let cancelled = false;
    setAddonPreviewLoading(true);
    setAddonPreview(null);
    companySubscriptionService.previewMyAddonPrice(addonDialogDef.id, addonQuantity, addonDuration)
      .then((preview) => { if (!cancelled) setAddonPreview(preview); })
      .catch((err) => { if (!cancelled) { console.error(err); toast.error(err.message || 'Failed to get price'); } })
      .finally(() => { if (!cancelled) setAddonPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [addonDialogOpen, addonDialogDef?.id, addonQuantity, addonDuration]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Company subscription (DB) is source of truth for Trial vs Active: is_trial=1 → Trial, active=1 & is_trial=0 → Active
      const [tenantSub, companyStatus, usageSummary] = await Promise.all([
        subscriptionService.getCurrentSubscription().catch(() => null),
        companySubscriptionService.getSubscriptionStatus().catch(() => null),
        usageService.getUsageSummary().catch(() => null)
      ]);

      let subData = tenantSub;
      if (!subData && companyStatus && (companyStatus.active || companyStatus.isTrial)) {
        const plans = await subscriptionService.getSubscriptionPlans().catch(() => []);
        const matchedPlan =
          plans.find(p => p.planCode === companyStatus.planCode) ||
          plans.find(p => p.name === companyStatus.planName) ||
          null;
        const planObj = matchedPlan || {
          name: companyStatus.planName || 'Current Plan',
          description: '',
          priceMonthly: 0,
          priceYearly: 0,
          currency: 'USD',
          trialDays: 0,
          maxUsers: -1,
          maxLocations: -1,
          maxProducts: -1,
          maxMonthlyOrders: -1,
          maxStorageGb: -1,
          features: {}
        };
        const status =
          companyStatus.isTrial ? 'TRIAL' :
            companyStatus.active ? 'ACTIVE' : 'CANCELLED';
        subData = {
          subscriptionId: companyStatus.subscriptionId,
          plan: planObj,
          status,
          trialStartDate: companyStatus.trialStartDate || null,
          trialEndDate: companyStatus.trialEndDate || null,
          currentPeriodStart: companyStatus.subscriptionStartDate || null,
          currentPeriodEnd: companyStatus.subscriptionEndDate || null,
          isInTrial: !!companyStatus.isTrial,
          trialDaysRemaining: Number(companyStatus.daysRemaining || 0)
        };
      }

      // Prefer company_subscriptions (DB) for Trial vs Active and for cycle dates (subscription_start/end, trial_start/end)
      if (companyStatus && subData) {
        const derivedStatus = companyStatus.isTrial ? 'TRIAL' : (companyStatus.active ? 'ACTIVE' : subData.status);
        const derivedIsInTrial = !!companyStatus.isTrial;
        const periodStart = derivedIsInTrial
          ? (companyStatus.trialStartDate ?? subData.trialStartDate ?? subData.currentPeriodStart)
          : (companyStatus.subscriptionStartDate ?? subData.currentPeriodStart);
        const periodEnd = derivedIsInTrial
          ? (companyStatus.trialEndDate ?? subData.trialEndDate ?? subData.currentPeriodEnd)
          : (companyStatus.subscriptionEndDate ?? subData.currentPeriodEnd);
        subData = {
          ...subData,
          status: derivedStatus,
          isInTrial: derivedIsInTrial,
          trialDaysRemaining: subData.trialDaysRemaining ?? 0,
          trialStartDate: companyStatus.trialStartDate ?? subData.trialStartDate,
          trialEndDate: companyStatus.trialEndDate ?? subData.trialEndDate,
          currentPeriodStart: periodStart,
          currentPeriodEnd: periodEnd
        };
      } else if (subData) {
        subData = {
          ...subData,
          isInTrial: subData.isInTrial || subData.status === 'TRIAL',
          trialDaysRemaining: subData.trialDaysRemaining ?? 0
        };
      }
      setSubscription(subData);
      const planLimits = subData?.plan || {};
      const addonBonuses = await companySubscriptionService.getAddonBonuses().catch(() => ({ USERS: 0, LOCATIONS: 0, PRODUCTS: 0 }));

      let usersCount = 0;
      let locationsCount = 0;
      let productsCount = 0;
      try {
        const usersResp = await axiosInstance.get('/users', { params: { page: 0, size: 1 } });
        usersCount =
          usersResp.data?.totalElements ??
          (Array.isArray(usersResp.data?.content) ? usersResp.data.content.length : 0);
      } catch (e) {
        usersCount = 0;
      }
      try {
        const locResp = await locationService.list({ page: 0, size: 1, sort: 'name', direction: 'ASC' });
        locationsCount =
          locResp?.totalElements ??
          (Array.isArray(locResp?.content) ? locResp.content.length : 0);
      } catch (e) {
        locationsCount = 0;
      }
      try {
        const prodResp = await productService.getAllProductsForManagement(0, 1, 'name', 'asc');
        productsCount =
          prodResp?.totalElements ??
          (Array.isArray(prodResp?.content) ? prodResp.content.length : 0);
        if (!productsCount) {
          const allResp = await axios.get(`${API_CONFIG.BASE_URL}/products/all`, { headers: getAuthHeader() });
          productsCount = Array.isArray(allResp.data) ? allResp.data.length : 0;
        }
      } catch (e) {
        productsCount = 0;
      }
      const buildMetric = (type, limitKey, addonBonus = 0) => {
        const planLimit = typeof planLimits[limitKey] === 'number' ? planLimits[limitKey] : -1;
        const effectiveLimit = planLimit === -1 ? -1 : (Number(planLimit) || 0) + (Number(addonBonus) || 0);
        const summaryCurrent = usageSummary && usageSummary.metrics && usageSummary.metrics[type] && usageSummary.metrics[type].current;
        const directCount =
          type === 'USERS' ? usersCount :
            type === 'LOCATIONS' ? locationsCount :
              type === 'PRODUCTS' ? productsCount : 0;
        const currentVal = (typeof directCount === 'number' && directCount > 0)
          ? directCount
          : (typeof summaryCurrent === 'number' ? summaryCurrent : 0);
        const percentage = effectiveLimit === -1 ? 0 : Math.min(100, ((Number(currentVal) || 0) / Math.max(1, Number(effectiveLimit))) * 100);
        return {
          metricType: type,
          current: Number(currentVal) || 0,
          limit: Number(effectiveLimit),
          percentage,
          planLimit: planLimit === -1 ? null : Number(planLimit),
          addonBonus: Number(addonBonus) || 0
        };
      };
      const usersMetric = buildMetric('USERS', 'maxUsers', addonBonuses?.USERS ?? 0);
      const locationsMetric = buildMetric('LOCATIONS', 'maxLocations', addonBonuses?.LOCATIONS ?? 0);
      const productsMetric = buildMetric('PRODUCTS', 'maxProducts', addonBonuses?.PRODUCTS ?? 0);
      const metrics = {
        USERS: usersMetric,
        LOCATIONS: locationsMetric,
        PRODUCTS: productsMetric
      };
      const composedUsage = { metrics };
      setUsageData(composedUsage);
    } catch (err) {
      console.error('Error loading subscription data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (newPlan) => {
    try {
      setActionLoading(true);
      await subscriptionService.changeSubscriptionPlan(newPlan.id, true);
      toast.success('Plan upgraded successfully!');
      setUpgradeDialogOpen(false);
      loadData(); // Reload data
    } catch (error) {
      console.error('Error upgrading plan:', error);
      toast.error(error.message || 'Failed to upgrade plan');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async (reason) => {
    try {
      setActionLoading(true);
      await subscriptionService.cancelSubscription(reason);
      toast.success('Subscription cancelled successfully');
      setCancelDialogOpen(false);
      loadData(); // Reload data
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error(error.message || 'Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpgradeFromBanner = () => {
    setUpgradeDialogOpen(true);
  };

  const loadAddons = async () => {
    try {
      setAddonsLoading(true);
      const [defs, addons] = await Promise.all([
        companySubscriptionService.getActiveAddonDefinitions().catch(() => []),
        companySubscriptionService.getMyAddons().catch(() => [])
      ]);
      setAddonDefinitions(Array.isArray(defs) ? defs : []);
      setMyAddons(Array.isArray(addons) ? addons : []);
    } catch (err) {
      console.error('Error loading addons:', err);
      toast.error('Failed to load add-ons');
    } finally {
      setAddonsLoading(false);
    }
  };

  const formatAddonCurrency = (amount, currency) => {
    if (amount == null) return '—';
    return formatCurrency(amount, { currencyCode: currency || 'INR' });
  };

  // Parse backend date (array [y,m,d,...] or ISO string) for add-on effectiveUntil
  const parseAddonDate = (value) => {
    if (value == null) return null;
    if (Array.isArray(value) && value.length >= 3) {
      const [y, m, d, h = 0, min = 0, s = 0] = value;
      return new Date(y, m - 1, d, h, min, s || 0);
    }
    if (typeof value === 'string') {
      const iso = value.includes(' ') && !value.includes('T') ? value.replace(' ', 'T') : value;
      const d = new Date(iso);
      return isNaN(d.getTime()) ? null : d;
    }
    if (value instanceof Date) return value;
    return null;
  };
  const formatAddonDate = (value) => {
    const d = parseAddonDate(value);
    if (!d) return 'plan end';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleOpenAddonDialog = (def) => {
    setAddonDialogDef(def);
    setAddonQuantity(1);
    setAddonDuration('REMAINING');
    setAddonPreview(null);
    setAddonDialogOpen(true);
  };

  const handleCloseAddonDialog = () => {
    if (!addonPaymentLoading) {
      setAddonDialogOpen(false);
      setAddonDialogDef(null);
      setAddonPreview(null);
    }
  };

  const handleAddonProceedToPay = async () => {
    if (!addonDialogDef?.id || !addonPreview?.amountToCharge) {
      toast.error('Please wait for price preview');
      return;
    }
    try {
      setAddonPaymentLoading(true);
      const orderData = await createAddonOrder({
        addonDefinitionId: addonDialogDef.id,
        quantityUnits: addonQuantity,
        durationType: addonDuration,
      });
      if (!window.Razorpay) {
        toast.error('Payment gateway not loaded. Please refresh and try again.');
        return;
      }
      const options = {
        key: orderData.keyId,
        amount: Math.round(Number(orderData.amount) * 100),
        currency: orderData.currency || 'INR',
        name: 'Tiya Technology',
        description: `Add-on: ${orderData.addonDefinitionName || orderData.planName} (${addonQuantity} unit(s))`,
        order_id: orderData.orderId,
        handler: async function (response) {
          try {
            setAddonPaymentLoading(true);
            const verifyResponse = await verifyPayment({
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature,
            });
            if (verifyResponse.status === 'success') {
              if (verifyResponse.addonCreated) {
                toast.success('Payment successful! Add-on has been added to your account.');
                setAddonDialogOpen(false);
                setAddonDialogDef(null);
                setAddonPreview(null);
                loadAddons();
              } else {
                toast.success('Payment verified successfully.');
              }
            } else {
              toast.error(verifyResponse.message || 'Payment verification failed');
            }
          } catch (err) {
            console.error('Verify error:', err);
            toast.error(err.message || 'Payment verification failed');
          } finally {
            setAddonPaymentLoading(false);
          }
        },
        theme: { color: '#667eea' },
        modal: { ondismiss: () => toast.info('Payment cancelled') },
      };
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      console.error('Create addon order error:', err);
      toast.error(err.message || 'Failed to start payment');
    } finally {
      setAddonPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress size={60} />
          </Box>
        </Container>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="error">
            {error}
          </Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Page Header */}
        <Box mb={4}>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Subscription & Usage
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your subscription, monitor usage, and upgrade your plan.
          </Typography>
        </Box>

        {/* Tabs */}
        <Box sx={{ mb: 4 }}>
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => setCurrentTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': {
                display: 'none', // Hide default indicator
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                color: 'text.secondary',
                minHeight: 48,
                borderRadius: 50, // Pill shape
                mr: 1,
                padding: '8px 20px',
                transition: 'all 0.2s',
                fontFamily: 'Roboto',
                '&.Mui-selected': {
                  color: 'primary.contrastText',
                  backgroundColor: 'primary.main',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                },
                '&:hover:not(.Mui-selected)': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: 'primary.main',
                }
              }
            }}
          >
            <Tab icon={<CreditCardIcon />} iconPosition="start" label="Current Plan" />
            <Tab icon={<TrendingUpIcon />} iconPosition="start" label="Usage & Limits" />
            <Tab icon={<AddonIcon />} iconPosition="start" label="Add-ons" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {currentTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <CurrentSubscriptionCard
                subscription={subscription}
                onUpgrade={() => { }}
                onCancel={() => setCancelDialogOpen(true)}
                loading={actionLoading}
              />
            </Grid>
          </Grid>
        )}

        {currentTab === 1 && (
          <Box>
            {subscription && (
              <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <ScheduleIcon color="primary" />
                    <Typography variant="h6" fontWeight={600} fontFamily="Roboto">
                      Billing Period Duration
                    </Typography>
                  </Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.success.light, 0.1), borderColor: alpha(theme.palette.success.main, 0.3) }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Days Used
                        </Typography>
                        <Typography variant="h4" fontWeight={700} color="success.main" fontFamily="Roboto">
                          {(() => {
                            const parse = (v) => { if (!v) return null; if (Array.isArray(v)) return new Date(v[0], v[1] - 1, v[2]); return new Date(v); };
                            const inTrial = subscription.isInTrial || subscription.status === 'TRIAL';
                            const ps = inTrial ? subscription.trialStartDate : subscription.currentPeriodStart;
                            const pe = inTrial ? subscription.trialEndDate : subscription.currentPeriodEnd;
                            const s = parse(ps);
                            const e = parse(pe);
                            if (!s) return '-';
                            const total = e ? Math.max(1, Math.round((e - s) / 86400000)) : 0;
                            const used = Math.min(total, Math.floor((Date.now() - s) / 86400000) + 1);
                            return used;
                          })()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Days passed in current cycle
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.info.light, 0.1), borderColor: alpha(theme.palette.info.main, 0.3) }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Days Remaining
                        </Typography>
                        <Typography variant="h4" fontWeight={700} color="info.main" fontFamily="Roboto">
                          {(() => {
                            const parse = (v) => { if (!v) return null; if (Array.isArray(v)) return new Date(v[0], v[1] - 1, v[2]); return new Date(v); };
                            const inTrial = subscription.isInTrial || subscription.status === 'TRIAL';
                            const ps = inTrial ? subscription.trialStartDate : subscription.currentPeriodStart;
                            const pe = inTrial ? subscription.trialEndDate : subscription.currentPeriodEnd;
                            const s = parse(ps);
                            const e = parse(pe);
                            if (!e) return '-';
                            const total = s ? Math.max(1, Math.round((e - s) / 86400000)) : 0;
                            const used = s ? Math.min(total, Math.floor((Date.now() - s) / 86400000) + 1) : 0;
                            return Math.max(0, total - used);
                          })()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Until next billing date
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}
            {usageData && (
              <UsageWidget
                usageData={usageData}
                loading={false}
                onUpgradeClick={undefined}
              />
            )}
          </Box>
        )}

        {currentTab === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontFamily="Roboto" fontWeight={600}>
                    Enhance your plan with Add-ons
                  </Typography>
                  {!subscription || isTrialUser ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Add-ons are available once you activate a plan. Activate your subscription to purchase extra users, locations, or products.
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Easily add extra users, locations, or products to your current subscription.
                      </Typography>
                      {addonsLoading ? (
                        <Box display="flex" justifyContent="center" py={4}>
                          <CircularProgress />
                        </Box>
                      ) : (
                        <>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                            Your add-ons
                          </Typography>
                          {myAddons.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No add-ons purchased yet.
                            </Typography>
                          ) : (
                            <List dense>
                              {myAddons.map((a) => (
                                <ListItem key={a.id} divider>
                                  <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
                                  <ListItemText
                                    primary={a.addonDefinitionName || 'Add-on'}
                                    secondary={`${a.quantityUnits} × until ${formatAddonDate(a.effectiveUntil)} • ${formatAddonCurrency(a.amountPaid, a.currency)}`}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          )}
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3, mb: 1 }}>
                            Available add-ons
                          </Typography>
                          {(() => {
                            // Hide FEATURE add-ons for regular users; only superadmin can assign them (can remove filter to show again)
                            const visibleAddonDefinitions = (addonDefinitions || []).filter(
                              (def) => def.metricType !== 'FEATURE'
                            );
                            return visibleAddonDefinitions.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                No add-on options available at the moment.
                              </Typography>
                            ) : (
                              <Grid container spacing={2}>
                                {visibleAddonDefinitions.map((def) => (
                                <Grid item xs={12} sm={6} md={4} key={def.id}>
                                  <Card
                                    variant="outlined"
                                    sx={{
                                      height: '100%',
                                      borderRadius: 3,
                                      transition: 'transform 0.2s, box-shadow 0.2s',
                                      '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: theme.shadows[4],
                                        borderColor: 'primary.main'
                                      }
                                    }}
                                  >
                                    <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderBottom: '1px solid', borderColor: 'divider' }}>
                                      <Typography variant="h6" fontWeight={700} fontFamily="Roboto">
                                        {def.name}
                                      </Typography>
                                    </Box>
                                    <CardContent>
                                      <Box mb={2}>
                                        <Chip label={def.metricType} size="small" sx={{ borderRadius: 1, fontWeight: 600 }} />
                                      </Box>

                                      <Typography variant="body2" color="text.secondary" paragraph>
                                        Increases your limit by <strong>{def.quantityPerUnit}</strong> per unit purchased.
                                      </Typography>

                                      <Divider sx={{ my: 2 }} />

                                      <Typography variant="h5" color="primary" fontWeight={700} fontFamily="Roboto">
                                        {formatAddonCurrency(def.priceYearly, def.currency)}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        per year
                                      </Typography>

                                      <Button
                                        variant="contained"
                                        fullWidth
                                        sx={{ mt: 3, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                                        onClick={() => handleOpenAddonDialog(def)}
                                        startIcon={<AddonIcon />}
                                      >
                                        Add to Plan
                                      </Button>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>
                            );
                          })()}
                        </>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Dialogs */}
        <PlanUpgradeDialog
          open={upgradeDialogOpen}
          onClose={() => setUpgradeDialogOpen(false)}
          currentPlan={subscription?.plan}
          onUpgrade={handleUpgrade}
          loading={actionLoading}
        />

        <CancellationDialog
          open={cancelDialogOpen}
          onClose={() => setCancelDialogOpen(false)}
          onConfirm={handleCancel}
          loading={actionLoading}
        />

        {/* Add-on purchase dialog */}
        <Dialog open={addonDialogOpen} onClose={handleCloseAddonDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Get add-on{addonDialogDef ? `: ${addonDialogDef.name}` : ''}</DialogTitle>
          <DialogContent>
            {addonDialogDef && (
              <Box sx={{ pt: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {addonDialogDef.metricType} • {addonDialogDef.quantityPerUnit} per unit • {formatAddonCurrency(addonDialogDef.priceYearly, addonDialogDef.currency)}/year
                </Typography>
                <FormControl component="fieldset" sx={{ mt: 2, display: 'block' }}>
                  <FormLabel component="legend">Duration</FormLabel>
                  <RadioGroup
                    row
                    value={addonDuration}
                    onChange={(e) => setAddonDuration(e.target.value)}
                  >
                    <FormControlLabel value="REMAINING" control={<Radio />} label="Until plan end (pro-rated)" />
                    <FormControlLabel value="FULL_PERIOD" control={<Radio />} label="Until add-on period end (full price)" />
                  </RadioGroup>
                </FormControl>
                <TextField
                  label="Number of units"
                  type="number"
                  fullWidth
                  sx={{ mt: 2 }}
                  value={addonQuantity}
                  onChange={(e) => setAddonQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  inputProps={{ min: 1 }}
                />
                {addonPreviewLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Calculating price…</Typography>
                  </Box>
                )}
                {!addonPreviewLoading && addonPreview && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Amount to pay</Typography>
                    <Typography variant="h6" color="primary">
                      {formatAddonCurrency(addonPreview.amountToCharge, addonPreview.currency)}
                    </Typography>
                    {addonPreview.effectiveUntil && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Valid until: {formatAddonDate(addonPreview.effectiveUntil)}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddonDialog} disabled={addonPaymentLoading}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleAddonProceedToPay}
              disabled={addonPaymentLoading || addonPreviewLoading || !addonPreview?.amountToCharge}
            >
              {addonPaymentLoading ? 'Opening…' : 'Proceed to Pay'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default SubscriptionDashboard;
